'use client';

import { useState, useRef, useEffect } from 'react';
import { Check, PlusCircle, ChevronDown } from 'lucide-react';

import { CreatableSelectProps } from './types';

import { Button } from 'components/ui/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from 'components/ui/command';
import { cn } from 'lib/utils';

export const CreatableSelect = ({ onChange, options, value }: CreatableSelectProps) => {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  const selected = options.find((opt) => opt.value === value);

  const handleSelect = (val: string) => {
    onChange(val);
    setOpen(false);
  };

  const handleCreate = () => {
    if (!input.trim()) return;
    onChange(input.trim());
    setOpen(false);
  };

  // Закрытие при клике вне компонента
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  return (
    <div className="relative w-full" ref={containerRef}>
      <Button
        variant="outline"
        role="combobox"
        aria-expanded={open}
        className="w-full justify-between"
        onClick={() => setOpen((prev) => !prev)}
      >
        {selected ? selected.label : value || 'Выберите параметр'}
        <ChevronDown className="ml-2 h-4 w-4 opacity-50" />
      </Button>

      {open && (
        <div className="absolute z-[100] mt-1 w-full rounded-md border bg-popover text-popover-foreground shadow-md">
          <Command>
            <CommandInput placeholder="Введите или выберите..." value={input} onValueChange={setInput} autoFocus />
            <CommandEmpty className="!py-2">
              <div
                onClick={handleCreate}
                className="flex items-center gap-2 !p-2 cursor-pointer hover:bg-accent text-sm text-muted-foreground"
              >
                <PlusCircle className="w-4 h-4" />
                Использовать своё: <span className="font-medium">{input}</span>
              </div>
            </CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem key={option.value} value={option.label} onSelect={() => handleSelect(option.value)}>
                  <Check className={cn('mr-2 h-4 w-4', option.value === value ? 'opacity-100' : 'opacity-0')} />
                  {option.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </Command>
        </div>
      )}
    </div>
  );
};
